/**
 * The USD figure shown beside the INR price of an IP Intel report:
 * "₹40,000 + GST · or USD 500 (no GST)" (owner, 23 Oct 2026).
 *
 * DISPLAY ONLY. Checkout, orders and Razorpay stay in INR; nothing here is
 * ever sent to the server or charged. The amount and its note are
 * admin-editable (Admin → IP Intel Report → Pricing) and arrive with the
 * public config as `usd_price` / `usd_note` (GET /api/v1/ip-intel/config,
 * and the IRCC programme config). An older server, or a value that does not
 * look right, falls back to USD 500 (no GST). Pure: no imports.
 */

export const USD_DEFAULT_PRICE = 500;
export const USD_DEFAULT_NOTE = 'no GST';
const USD_MAX = 1_000_000;
const NOTE_MAX = 40;

export interface UsdSource {
  usd_price?: unknown;
  usd_note?: unknown;
}

export interface UsdPrice {
  /** Whole US dollars. */
  price: number;
  /** "no GST"; may be empty (the admin cleared it). */
  note: string;
  /** "USD 500" */
  amount: string;
  /** "USD 500 (no GST)" — or "USD 500" with no note. */
  label: string;
}

export function usdPrice(src?: UsdSource | null): UsdPrice {
  const raw = src ? src.usd_price : undefined;
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' && /^\s*\d{1,9}\s*$/.test(raw) ? Number(raw) : NaN;
  const price = Number.isInteger(n) && n >= 1 && n <= USD_MAX ? n : USD_DEFAULT_PRICE;
  const rawNote = src ? src.usd_note : undefined;
  // Absent (older server) → the default; an explicit '' means "no note".
  const note = typeof rawNote === 'string'
    ? (rawNote.replace(/\s+/g, ' ').trim().length <= NOTE_MAX ? rawNote.replace(/\s+/g, ' ').trim() : USD_DEFAULT_NOTE)
    : USD_DEFAULT_NOTE;
  const amount = `USD ${price.toLocaleString('en-US')}`;
  return { price, note, amount, label: note ? `${amount} (${note})` : amount };
}

/** "or USD 500 (no GST)" — the secondary line under a price. */
export const usdAlt = (src?: UsdSource | null): string => `or ${usdPrice(src).label}`;

/** "₹40,000 + GST · or USD 500 (no GST)" — the INR label with the USD one. */
export const withUsd = (inrLabel: string, src?: UsdSource | null): string =>
  inrLabel ? `${inrLabel} · ${usdAlt(src)}` : '';
